import React, { useState, useMemo } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { CheckCircle2, Circle, Clock, AlertTriangle, Filter, Calendar, User, TrendingUp, ListTodo, FileCheck } from 'lucide-react';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import ActiveProjectBadge from '../components/ActiveProjectBadge';
import { NextStepButton } from '../components/WorkflowNavigation';
import { DraftTask, FmeaStandard } from '../types';

// AIAG-VDA 7 Steps
const WORKFLOW_STEPS = [
    { step: 1, name: 'Planning', shortName: 'Plan' },
    { step: 2, name: 'Structure Analysis', shortName: 'Structure' },
    { step: 3, name: 'Function Analysis', shortName: 'Function' },
    { step: 4, name: 'Failure Analysis', shortName: 'Failure' },
    { step: 5, name: 'Risk Analysis', shortName: 'Risk' },
    { step: 6, name: 'Optimization', shortName: 'Optimize' },
    { step: 7, name: 'Documentation', shortName: 'Docs' },
];

// Tab types
type TabType = 'all' | 'myTasks' | 'drafts';

interface ActionItem {
    id: string;
    fmeaId: string;
    failureMode: string;
    function?: string;
    actionType: 'prevention' | 'detection' | 'design_change' | 'validation';
    description: string;
    assignee?: string;
    dueDate?: string;
    status: 'planned' | 'in_progress' | 'completed' | 'blocked';
    priority: 'high' | 'medium' | 'low';
    rpnBefore: number;
    rpnTarget: number;
    rpnActual?: number;
    notes?: string;
    projectName?: string;
    epochId?: number;
    workflowStep?: number;
    fmeaStandard?: FmeaStandard;
}

// Step progress calculation
interface StepProgress {
    step: number;
    name: string;
    complete: number;
    total: number;
    percentage: number;
}

const ActionTrackingDashboard: React.FC = () => {
    const { allData, activeProject, currentUser } = useAppContext();

    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [filterPriority, setFilterPriority] = useState<string>('all');
    const [filterType, setFilterType] = useState<string>('all');
    const [activeTab, setActiveTab] = useState<TabType>('all');

    // Note: draftTasks state removed - implement when backend integration is ready
    const draftTasks: DraftTask[] = []; // Static empty array for now

    // Calculate step progress from FMEA data
    const stepProgress = useMemo((): StepProgress[] => {
        const projectFmeas = (allData?.fmeaItems || []).filter(
            f => !activeProject || activeProject === 'all' || f.projectName === activeProject
        );

        // For now, calculate based on field completion
        // Step 1-2: Structure (has function)
        // Step 3: Function (has requirement)
        // Step 4: Failure (has failureMode, effects)
        // Step 5: Risk (has S, O, D)
        // Step 6: Optimization (has controls)
        // Step 7: Documentation (status = active)

        const total = projectFmeas.length || 1;

        return WORKFLOW_STEPS.map(step => {
            let complete = 0;

            switch (step.step) {
                case 1: complete = projectFmeas.length > 0 ? total : 0; break;
                case 2: complete = projectFmeas.filter(f => f.function || f.itemFunction).length; break;
                case 3: complete = projectFmeas.filter(f => f.requirement).length; break;
                case 4: complete = projectFmeas.filter(f => f.failureMode && f.effects).length; break;
                case 5: complete = projectFmeas.filter(f => f.S && f.O && f.D).length; break;
                case 6: complete = projectFmeas.filter(f => (f.preventionControls?.length || 0) > 0 || (f.detectionControls?.length || 0) > 0).length; break;
                case 7: complete = projectFmeas.filter(f => f.status === 'active').length; break;
            }

            return {
                step: step.step,
                name: step.name,
                complete,
                total,
                percentage: Math.round((complete / total) * 100)
            };
        });
    }, [allData, activeProject]);


    // Generate action items from FMEA data
    const actionItems = useMemo((): ActionItem[] => {
        const items: ActionItem[] = [];

        (allData?.fmeaItems || []).forEach(fmea => {
            // Filter by active project
            if (activeProject && activeProject !== 'all' && fmea.projectName !== activeProject) {
                return;
            }

            // Only include items with high RPN or action priority
            const rpn = fmea.RPN || (fmea.S || 1) * (fmea.O || 1) * (fmea.D || 1);
            const priority = fmea.actionPriority || (rpn > 100 ? 'high' : rpn > 50 ? 'medium' : 'low');

            if (priority === 'high' || priority === 'medium') {
                // Generate action item for high-risk failures
                items.push({
                    id: `ACTION-${fmea.id}`,
                    fmeaId: fmea.id,
                    failureMode: fmea.failureMode || 'Unknown',
                    actionType: 'prevention',
                    description: `Reduce occurrence of: ${fmea.failureMode}`,
                    status: fmea.status === 'active' ? 'planned' : 'completed',
                    priority: priority as 'high' | 'medium' | 'low',
                    rpnBefore: rpn,
                    rpnTarget: Math.floor(rpn * 0.5), // Target 50% reduction
                    projectName: fmea.projectName,
                    epochId: fmea.epochId
                });
            }
        });

        return items;
    }, [allData, activeProject]);

    // Apply filters
    const filteredActions = useMemo(() => {
        return actionItems.filter(action => {
            if (filterStatus !== 'all' && action.status !== filterStatus) return false;
            if (filterPriority !== 'all' && action.priority !== filterPriority) return false;
            if (filterType !== 'all' && action.actionType !== filterType) return false;
            return true;
        });
    }, [actionItems, filterStatus, filterPriority, filterType]);

    // Statistics
    const stats = useMemo(() => {
        const total = actionItems.length;
        const completed = actionItems.filter(a => a.status === 'completed').length;
        const inProgress = actionItems.filter(a => a.status === 'in_progress').length;
        const blocked = actionItems.filter(a => a.status === 'blocked').length;
        const highPriority = actionItems.filter(a => a.priority === 'high').length;

        return { total, completed, inProgress, blocked, highPriority };
    }, [actionItems]);

    // Status icon
    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed':
                return <CheckCircle2 className="text-green-600" size={20} />;
            case 'in_progress':
                return <Clock className="text-blue-600" size={20} />;
            case 'blocked':
                return <AlertTriangle className="text-red-600" size={20} />;
            default:
                return <Circle className="text-gray-400" size={20} />;
        }
    };

    // Priority badge
    const getPriorityBadge = (priority: string) => {
        const colors = {
            high: 'bg-red-100 text-red-800 border-red-300',
            medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
            low: 'bg-green-100 text-green-800 border-green-300'
        };
        return (
            <span className={`px-2 py-1 rounded text-xs font-semibold border ${colors[priority as keyof typeof colors]}`}>
                {priority.toUpperCase()}
            </span>
        );
    };

    // Type badge
    const getTypeBadge = (type: string) => {
        const labels = {
            prevention: 'Prevention',
            detection: 'Detection',
            design_change: 'Design Change',
            validation: 'Validation'
        };
        return (
            <span className="px-2 py-1 rounded text-xs bg-primary/10 text-primary border border-primary/30">
                {labels[type as keyof typeof labels] || type}
            </span>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-text-primary flex items-center gap-2">
                        <TrendingUp className="text-primary" />
                        Action Tracking Dashboard
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                        <p className="text-text-secondary">Monitor and manage FMEA action items</p>
                        <ActiveProjectBadge showStandard={false} />
                    </div>
                </div>
                <ModuleInfoModal
                    title="Action Tracking Dashboard"
                    description="Centralized dashboard for tracking FMEA action items, mitigation plans, and risk reduction activities."
                    benefits={[
                        "Track progress on risk mitigation actions",
                        "Prioritize high-impact activities",
                        "Monitor RPN reduction over time",
                        "Ensure accountability with assignments and due dates"
                    ]}
                    usageInstructions={[
                        "Review action items by status (Planned, In Progress, Completed, Blocked)",
                        "Filter by priority (High, Medium, Low) or action type",
                        "Assign owners and set due dates for each action",
                        "Track actual RPN vs target RPN to measure effectiveness"
                    ]}
                />
            </div>

            {/* Step Progress (AIAG-VDA 7 Steps) */}
            <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <ListTodo className="text-primary" size={20} />
                        Workflow Progress (AIAG-VDA)
                    </h2>
                    <span className="text-sm text-text-secondary">
                        Overall: {Math.round(stepProgress.reduce((acc, s) => acc + s.percentage, 0) / 7)}%
                    </span>
                </div>
                <div className="flex gap-2">
                    {stepProgress.map(step => (
                        <div key={step.step} className="flex-1">
                            <div className="text-xs text-text-secondary text-center mb-1">
                                {step.step}. {WORKFLOW_STEPS.find(w => w.step === step.step)?.shortName}
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className={`h-full transition-all ${step.percentage === 100 ? 'bg-green-500' :
                                        step.percentage > 50 ? 'bg-blue-500' :
                                            step.percentage > 0 ? 'bg-yellow-500' : 'bg-gray-300'
                                        }`}
                                    style={{ width: `${step.percentage}%` }}
                                />
                            </div>
                            <div className="text-xs text-center mt-1 text-text-secondary">
                                {step.complete}/{step.total}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-border">
                <button
                    onClick={() => setActiveTab('all')}
                    className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'all'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-text-secondary hover:text-text-primary'
                        }`}
                >
                    <TrendingUp size={16} className="inline mr-2" />
                    All Tasks ({stats.total})
                </button>
                <button
                    onClick={() => setActiveTab('myTasks')}
                    className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'myTasks'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-text-secondary hover:text-text-primary'
                        }`}
                >
                    <User size={16} className="inline mr-2" />
                    My Tasks ({actionItems.filter(a => a.assignee === currentUser?.username).length})
                </button>
                <button
                    onClick={() => setActiveTab('drafts')}
                    className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'drafts'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-text-secondary hover:text-text-primary'
                        }`}
                >
                    <FileCheck size={16} className="inline mr-2" />
                    Draft Tasks ({draftTasks.length})
                </button>
            </div>

            {/* Statistics Cards */}

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-text-secondary">Total Actions</div>
                    <div className="text-3xl font-bold text-text-primary mt-1">{stats.total}</div>
                </div>
                <div className="bg-card border border-green-200 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-green-700">Completed</div>
                    <div className="text-3xl font-bold text-green-600 mt-1">{stats.completed}</div>
                    <div className="text-xs text-green-600 mt-1">
                        {stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}% complete
                    </div>
                </div>
                <div className="bg-card border border-blue-200 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-blue-700">In Progress</div>
                    <div className="text-3xl font-bold text-blue-600 mt-1">{stats.inProgress}</div>
                </div>
                <div className="bg-card border border-red-200 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-red-700">Blocked</div>
                    <div className="text-3xl font-bold text-red-600 mt-1">{stats.blocked}</div>
                </div>
                <div className="bg-card border border-orange-200 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-orange-700">High Priority</div>
                    <div className="text-3xl font-bold text-orange-600 mt-1">{stats.highPriority}</div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                    <Filter size={20} className="text-primary" />
                    <h2 className="text-lg font-semibold">Filters</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Status</label>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="w-full bg-background border border-border rounded p-2 text-sm"
                        >
                            <option value="all">All Statuses</option>
                            <option value="planned">Planned</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="blocked">Blocked</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Priority</label>
                        <select
                            value={filterPriority}
                            onChange={(e) => setFilterPriority(e.target.value)}
                            className="w-full bg-background border border-border rounded p-2 text-sm"
                        >
                            <option value="all">All Priorities</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Action Type</label>
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="w-full bg-background border border-border rounded p-2 text-sm"
                        >
                            <option value="all">All Types</option>
                            <option value="prevention">Prevention</option>
                            <option value="detection">Detection</option>
                            <option value="design_change">Design Change</option>
                            <option value="validation">Validation</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Action Items Table */}
            <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-background/50 border-b border-border">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Action</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Type</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Priority</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">RPN</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Target</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Reduction</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Assignee</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Due Date</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {filteredActions.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="px-4 py-8 text-center text-text-secondary">
                                        No action items found. Adjust filters or add high-priority FMEA items.
                                    </td>
                                </tr>
                            ) : (
                                filteredActions.map((action) => {
                                    const reduction = Math.round(((action.rpnBefore - action.rpnTarget) / action.rpnBefore) * 100);
                                    return (
                                        <tr key={action.id} className="hover:bg-background/50">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    {getStatusIcon(action.status)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div>
                                                    <div className="font-medium text-sm">{action.description}</div>
                                                    <div className="text-xs text-text-secondary mt-1">
                                                        FMEA: {action.failureMode}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">{getTypeBadge(action.actionType)}</td>
                                            <td className="px-4 py-3">{getPriorityBadge(action.priority)}</td>
                                            <td className="px-4 py-3">
                                                <span className="font-semibold text-red-600">{action.rpnBefore}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="font-semibold text-green-600">{action.rpnTarget}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-16 bg-gray-200 rounded-full h-2">
                                                        <div
                                                            className="bg-green-600 h-2 rounded-full"
                                                            style={{ width: `${Math.min(reduction, 100)}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="text-xs font-semibold text-green-600">-{reduction}%</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-1 text-sm text-text-secondary">
                                                    <User size={14} />
                                                    {action.assignee || 'Unassigned'}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-1 text-sm text-text-secondary">
                                                    <Calendar size={14} />
                                                    {action.dueDate || 'No date'}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Summary */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <p className="text-sm text-text-secondary">
                    <strong>💡 Tip:</strong> Focus on High Priority actions first. Target a 50% RPN reduction for critical items.
                    Assign owners and set realistic due dates to maintain momentum.
                </p>
            </div>

            {/* Continue to Next Step */}
            <div className={`p-4 mt-6 rounded-lg border flex items-center justify-between bg-card border-border`}>
                <div>
                    <h3 className="font-semibold text-text-primary">Ready to continue?</h3>
                    <p className="text-sm text-text-secondary">Proceed to generate results documentation and control plans</p>
                </div>
                <NextStepButton currentStep={6} isComplete={true} />
            </div>
        </div>
    );
};

export default ActionTrackingDashboard;
